import {
  Directive,
  HostListener,
  Inject,
  Injector,
  OnDestroy,
  OnInit,
  Optional,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { ComponentRegistryService } from '@services/component-registry/component-registry.service';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

/**
 * Abstract class that handles window beforeunload event and will block user navigating
 * away from site if canDeactivate returns true.
 * Automatically registers components with ComponentRegistryService for CanDeactivate guard checking.
 */
@Directive()
export abstract class ComponentCanDeactivate implements OnInit, OnDestroy {
  protected componentRegistryService?: ComponentRegistryService;
  protected routerForRegistration?: Router;
  private destroy$ = new Subject<void>();
  private isRegistered = false;
  private injector?: Injector;

  constructor(@Optional() injector?: Injector) {
    // Store injector to lazily get services (Directive DI doesn't work the same as Component DI)
    this.injector = injector;

    // Try to initialize immediately if injector is provided
    if (this.injector) {
      this.initializeServices();
    } else {
      // If no injector, try once synchronously using static instance
      // Retry logic will be handled in ngOnInit to avoid timer issues in tests
      this.componentRegistryService = ComponentRegistryService.getInstance();
      if (this.componentRegistryService) {
        const currentRoute = this.componentRegistryService.getCurrentRoute();
        if (currentRoute) {
          this.componentRegistryService.register(this, currentRoute);
          this.isRegistered = true;
        }
      }
    }
  }

  /**
   * Attempts to register component using static service instance.
   * This is called from ngOnInit to avoid timer issues in tests.
   */
  private tryRegisterWithStaticInstance(): void {
    if (this.isRegistered) {
      return;
    }

    this.componentRegistryService = ComponentRegistryService.getInstance();
    if (this.componentRegistryService) {
      const currentRoute = this.componentRegistryService.getCurrentRoute();
      if (currentRoute) {
        this.componentRegistryService.register(this, currentRoute);
        this.isRegistered = true;
      }
    }
  }

  /**
   * Initializes services using Injector since Directive DI doesn't work properly.
   * Can be called manually by components that inject Injector and pass it to super().
   */
  private initializeServices(): void {
    // Try to get services from injector first
    if (this.injector) {
      try {
        this.componentRegistryService = this.injector.get(ComponentRegistryService, null);
        this.routerForRegistration = this.injector.get(Router, null);
      } catch (error) {
        console.error('[ComponentCanDeactivate] Error getting services from injector:', error);
      }
    }

    // Fallback: try to get service from static instance
    if (!this.componentRegistryService) {
      this.componentRegistryService = ComponentRegistryService.getInstance();
    }

    // If we still don't have router, try to get it from injector
    if (!this.routerForRegistration && this.injector) {
      try {
        this.routerForRegistration = this.injector.get(Router, null);
      } catch (error) {
        // Ignore
      }
    }

    // Register component now that services are available
    if (this.componentRegistryService && this.routerForRegistration) {
      this.registerComponent();

      // Also listen for route changes to update registration
      this.routerForRegistration.events
        .pipe(
          filter((event) => event instanceof NavigationEnd),
          takeUntil(this.destroy$),
        )
        .subscribe(() => {
          this.registerComponent();
        });
    } else {
      console.warn('[ComponentCanDeactivate] Missing services, cannot register', {
        hasService: !!this.componentRegistryService,
        hasRouter: !!this.routerForRegistration,
      });
    }
  }

  /**
   * Public method for components to manually initialize registration.
   * Components can call this in their constructor or ngOnInit if they inject services.
   *
   * @param componentRegistryService - Optional service instance (if component injects it)
   * @param router - Optional router instance (if component injects it)
   */
  protected initializeRegistration(
    componentRegistryService?: ComponentRegistryService,
    router?: Router,
  ): void {
    if (componentRegistryService) {
      this.componentRegistryService = componentRegistryService;
    }
    if (router) {
      this.routerForRegistration = router;
    }

    // If we now have both services, initialize
    if (this.componentRegistryService && this.routerForRegistration && !this.isRegistered) {
      this.registerComponent();

      // Also listen for route changes to update registration
      if (this.routerForRegistration) {
        this.routerForRegistration.events
          .pipe(
            filter((event) => event instanceof NavigationEnd),
            takeUntil(this.destroy$),
          )
          .subscribe(() => {
            this.registerComponent();
          });
      }
    }
  }

  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: any) {
    // @ts-ignore: hack to get around Angular's strangle-hold on this event handler
    if (window.__user_is_inactive__) return;

    // Check the currently active component from the registry, not this instance
    // This ensures we check the correct component even after navigation
    const registryService = ComponentRegistryService.getInstance();
    if (registryService) {
      const currentRoute = registryService.getCurrentRoute();
      const activeComponent = registryService.getComponentForRoute(currentRoute);

      // Only check if this is the currently active component
      // This prevents stale components from blocking navigation
      if (activeComponent === this && !this.canDeactivate()) {
        $event.returnValue = true;
      }
    } else {
      // Fallback to checking this instance if registry is not available
      if (!this.canDeactivate()) {
        $event.returnValue = true;
      }
    }
  }

  /**
   * Registers the component with ComponentRegistryService.
   * Called automatically in constructor and on route changes.
   */
  private registerComponent(): void {
    if (this.componentRegistryService && this.routerForRegistration) {
      const currentRoute = this.routerForRegistration.url;
      if (currentRoute) {
        this.componentRegistryService.register(this, currentRoute);
        this.isRegistered = true;
      }
    }
  }

  /**
   * Auto-registers component with ComponentRegistryService.
   * This is a fallback in case constructor registration didn't work.
   * Components implementing OnInit should call super.ngOnInit() if they override this method.
   *
   * Components that inject ComponentRegistryService and Router can call:
   * super.initializeRegistration(this.componentRegistryService, this.router) in their constructor
   */
  ngOnInit(): void {
    // Always try to register in ngOnInit (components should call super.ngOnInit() if they override this)
    // This is more reliable than constructor since services are definitely available by then
    if (!this.isRegistered) {
      // Try to get services if we have injector
      if (this.injector) {
        this.initializeServices();
      } else {
        // No injector, try static instance (synchronously, no timers)
        this.tryRegisterWithStaticInstance();
      }
    }
    // Fallback: try to register if services are available
    if (!this.isRegistered && this.componentRegistryService && this.routerForRegistration) {
      this.registerComponent();
    }
  }

  /**
   * Auto-unregisters component from ComponentRegistryService.
   * Components implementing OnDestroy should call super.ngOnDestroy() if they override this method.
   */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.componentRegistryService) {
      this.componentRegistryService.unregister(this);
      this.isRegistered = false;
    }
  }

  abstract canDeactivate(): boolean;
}
